import { Decimal, money, roundMoney, type MoneyInput } from './money';

/**
 * Optional stake progression (e.g. multiply after a loss). This does NOT
 * recover losses with any certainty — it increases exposure. The table and
 * worst-case exposure are always shown before a session can start, and the
 * backend enforces `maxLevels` and the session max loss on every order.
 */
export interface ProgressionConfig {
  enabled: boolean;
  /** Multiplier applied after a loss, as a decimal string, e.g. "2.0". */
  multiplier: string;
  /** Number of progression steps after the base stake (0 = never increases). */
  maxLevels: number;
  /** What happens after a loss at the last level. A robot session always stops (see applySettlementToSession). */
  onMaxLevel: 'RESET' | 'STOP';
}

export const PROGRESSION_LIMITS = {
  minMultiplier: '1.1',
  maxMultiplier: '5',
  maxLevels: 10,
} as const;

/** Gales the robots (Automação) play: 1 to 8 (owner 2026-10-27). Rush has none. */
export const ROBOT_GALES = { min: 1, max: 8 } as const;

/** Thor, the aggressive automation: 6 to 8 gales (owner 2026-11-01). */
export const THOR_GALES = { min: 6, max: 8 } as const;

/** A robot's gales outside its range (ROBOT_GALES, or Thor's; any game but Rush). Null = fine. */
export function robotGalesError(cfg: ProgressionConfig, modality: string, range: { min: number; max: number } = ROBOT_GALES): string | null {
  if (modality === 'ACCUMULATOR') return null;
  const gales = cfg.enabled ? cfg.maxLevels : 0;
  if (gales >= range.min && gales <= range.max) return null;
  return range.max === range.min + 1 ? `Escolha ${range.min} ou ${range.max} gales.` : `Escolha de ${range.min} a ${range.max} gales.`;
}

export const NO_PROGRESSION: ProgressionConfig = { enabled: false, multiplier: '2', maxLevels: 0, onMaxLevel: 'RESET' };

export interface StakeLevel {
  level: number;
  stake: Decimal;
  /** Total lost if every trade from level 0 to this one loses. */
  cumulativeLoss: Decimal;
}

export function validateProgression(cfg: ProgressionConfig): string | null {
  if (!cfg.enabled) return null;
  const m = money(cfg.multiplier);
  if (m.lt(PROGRESSION_LIMITS.minMultiplier) || m.gt(PROGRESSION_LIMITS.maxMultiplier)) {
    return `Multiplicador deve estar entre ${PROGRESSION_LIMITS.minMultiplier} e ${PROGRESSION_LIMITS.maxMultiplier}.`;
  }
  if (!Number.isInteger(cfg.maxLevels) || cfg.maxLevels < 1 || cfg.maxLevels > PROGRESSION_LIMITS.maxLevels) {
    return `Níveis devem estar entre 1 e ${PROGRESSION_LIMITS.maxLevels}.`;
  }
  return null;
}

export function buildStakeTable(baseStake: MoneyInput, cfg: ProgressionConfig, currency = 'USD'): StakeLevel[] {
  const base = roundMoney(baseStake, currency);
  const levels = cfg.enabled ? cfg.maxLevels : 0;
  const mult = money(cfg.multiplier);
  const out: StakeLevel[] = [];
  let cumulative = new Decimal(0);
  for (let level = 0; level <= levels; level++) {
    const stake = roundMoney(base.times(mult.pow(level)), currency);
    cumulative = cumulative.plus(stake);
    out.push({ level, stake, cumulativeLoss: cumulative });
  }
  return out;
}

export function stakeForLevel(baseStake: MoneyInput, cfg: ProgressionConfig, level: number, currency = 'USD'): Decimal {
  const table = buildStakeTable(baseStake, cfg, currency);
  const clamped = Math.max(0, Math.min(level, table.length - 1));
  return table[clamped]!.stake;
}

/** Worst-case loss of one complete losing sequence. */
export function worstCaseSequenceLoss(baseStake: MoneyInput, cfg: ProgressionConfig, currency = 'USD'): Decimal {
  const table = buildStakeTable(baseStake, cfg, currency);
  return table[table.length - 1]!.cumulativeLoss;
}

export type Outcome = 'WIN' | 'LOSS' | 'NEUTRAL';

export function nextProgressionLevel(
  current: number,
  outcome: Outcome,
  cfg: ProgressionConfig,
): { level: number; stop: boolean } {
  if (outcome === 'WIN') return { level: 0, stop: false };
  if (outcome === 'NEUTRAL') return { level: cfg.enabled ? current : 0, stop: false };
  // Without gales every loss is the last one (a lost cycle).
  if (!cfg.enabled || current >= cfg.maxLevels) {
    return cfg.onMaxLevel === 'STOP' ? { level: current, stop: true } : { level: 0, stop: false };
  }
  return { level: current + 1, stop: false };
}
